import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Lock } from 'lucide-react';
import clsx from 'clsx';
import type { QueryResult } from '@idas/shared';

/**
 * Result grid.
 *
 * Row virtualisation is not an optimisation here, it is a requirement: the
 * server caps results at 10,000 rows and a table of that many DOM nodes makes
 * the tab unresponsive. Only the visible window is rendered.
 *
 * Column widths are fixed rather than content-derived, because measuring text
 * across 10,000 rows to auto-size is exactly the work virtualisation exists to
 * avoid. Columns are resizable by the user instead.
 */
const ROW_HEIGHT = 26;
const DEFAULT_COLUMN_WIDTH = 180;
const ROW_NUMBER_WIDTH = 52;

export function ResultGrid({ result }: { result: QueryResult }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: result.rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: result.columns.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => DEFAULT_COLUMN_WIDTH,
    overscan: 3,
  });

  if (result.columns.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-2xs text-ink-faint">
        This statement returned no columns.
      </div>
    );
  }

  const totalWidth = columnVirtualizer.getTotalSize() + ROW_NUMBER_WIDTH;

  return (
    <div ref={scrollRef} className="h-full overflow-auto bg-surface">
      <div style={{ width: totalWidth, position: 'relative' }}>
        {/* Header. Sticky so column names stay visible while scrolling 10k rows. */}
        <div
          className="sticky top-0 z-20 flex h-7 border-b border-edge-strong bg-surface-raised"
          style={{ width: totalWidth }}
        >
          <div
            className="sticky left-0 z-10 shrink-0 border-r border-edge bg-surface-raised"
            style={{ width: ROW_NUMBER_WIDTH }}
          />
          {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
            const column = result.columns[virtualColumn.index]!;
            return (
              <div
                key={virtualColumn.key}
                className="absolute flex h-7 items-center gap-1 border-r border-edge px-2"
                style={{
                  left: virtualColumn.start + ROW_NUMBER_WIDTH,
                  width: virtualColumn.size,
                }}
                title={`${column.name} · ${column.dataType}${column.masked ? ' · masked by policy' : ''}`}
              >
                <span className="truncate text-2xs font-semibold text-ink">{column.name}</span>
                {column.masked && <Lock className="h-2.5 w-2.5 shrink-0 text-lock" />}
                <span className="ml-auto shrink-0 font-mono text-2xs text-ink-faint">
                  {column.dataType}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = result.rows[virtualRow.index]!;
            return (
              <div
                key={virtualRow.key}
                className={clsx(
                  'absolute flex border-b border-edge/50 hover:bg-accent/5',
                  // Zebra striping: at this row height, tracking a value across
                  // 30 columns is genuinely hard without it.
                  virtualRow.index % 2 === 1 && 'bg-surface-raised/40',
                )}
                style={{
                  top: virtualRow.start,
                  height: virtualRow.size,
                  width: totalWidth,
                }}
              >
                <div
                  className="sticky left-0 z-10 flex shrink-0 items-center justify-end border-r border-edge bg-surface px-2 font-mono text-2xs tabular-nums text-ink-faint"
                  style={{ width: ROW_NUMBER_WIDTH }}
                >
                  {virtualRow.index + 1}
                </div>

                {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                  const value = row[virtualColumn.index];
                  const masked = result.columns[virtualColumn.index]?.masked;
                  return (
                    <Cell
                      key={virtualColumn.key}
                      value={value}
                      masked={masked}
                      left={virtualColumn.start + ROW_NUMBER_WIDTH}
                      width={virtualColumn.size}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Cell({
  value,
  masked,
  left,
  width,
}: {
  value: unknown;
  masked?: boolean;
  left: number;
  width: number;
}) {
  const isNull = value === null || value === undefined;
  const isNumber = typeof value === 'number';

  return (
    <div
      className="absolute flex h-full items-center border-r border-edge/50 px-2"
      style={{ left, width }}
      // Full value on hover: truncation is unavoidable at this density, but
      // silently hiding data from someone reading a report is not acceptable.
      title={isNull ? 'NULL' : String(value)}
    >
      <span
        className={clsx(
          'truncate font-mono text-2xs',
          isNull && 'italic text-ink-faint',
          masked && 'text-lock',
          // Right-align numbers so magnitudes line up and a stray order of
          // magnitude is visible at a glance.
          isNumber && 'ml-auto tabular-nums',
          !isNull && !masked && 'text-ink',
        )}
      >
        {isNull ? 'NULL' : String(value)}
      </span>
    </div>
  );
}
